import { Button } from '@sunstone/ui';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { CategorySection } from '@/features/help/CategorySection';
import { helpCategories } from '@/features/help/content';
import { HelpSearchBar } from '@/features/help/HelpSearchBar';
import { HelpToc } from '@/features/help/HelpToc';
import { StillStuckCallout } from '@/features/help/StillStuckCallout';
import { filterHelpCategories } from '@/features/help/search';
import { useScrollSpy } from '@/features/help/useScrollSpy';
import { useDebounce } from '@/hooks/useDebounce';

const ALL_SLUGS = helpCategories.flatMap((category) => category.questions.map((q) => q.slug));

function findSlugCategory(slug: string) {
  return helpCategories.some((category) => category.questions.some((q) => q.slug === slug));
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Sunstone Help page (SUN-412 / help redesign).
 *
 * A searchable, categorized FAQ. Content lives in `@/features/help/content` (a .ts module, since
 * the UI container build excludes markdown from its build context) and each answer renders via
 * the shared MarkdownRenderer. Add a question to `content.ts` as questions come up.
 */
export function HelpPage() {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);
  const [openSlugs, setOpenSlugs] = useState<string[]>(ALL_SLUGS);

  const filteredCategories = useMemo(
    () => filterHelpCategories(helpCategories, debouncedQuery),
    [debouncedQuery],
  );
  const categoryIds = useMemo(
    () => filteredCategories.map((category) => category.id),
    [filteredCategories],
  );
  const activeCategoryId = useScrollSpy(categoryIds);
  const allExpanded = ALL_SLUGS.length > 0 && ALL_SLUGS.every((slug) => openSlugs.includes(slug));

  // Auto-expand whatever currently matches the search, without forcing anything the user opened
  // outside of search to collapse.
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      return;
    }
    const matchingSlugs = filteredCategories.flatMap((category) =>
      category.questions.map((question) => question.slug),
    );
    setOpenSlugs((prev) => Array.from(new Set([...prev, ...matchingSlugs])));
  }, [debouncedQuery, filteredCategories]);

  // Deep link support: `/help#<slug>` opens and scrolls to that question on load.
  useEffect(() => {
    const slug = window.location.hash.replace('#', '');
    if (!slug || !findSlugCategory(slug)) {
      return;
    }
    setOpenSlugs((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
    const timeout = setTimeout(() => scrollToId(slug), 50);
    return () => clearTimeout(timeout);
    // Intentionally mount-only — this reads the hash present when the page first loads.
  }, []);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Help"
        description="Common questions about using Sunstone. A living page — add to it as questions come up."
      />

      <HelpSearchBar value={query} onChange={setQuery} />

      <HelpToc
        categories={filteredCategories}
        activeId={activeCategoryId}
        onSelect={scrollToId}
        orientation="horizontal"
        className="lg:hidden"
      />

      <div className="grid gap-8 lg:grid-cols-[1fr_220px]">
        <div className="space-y-8">
          {filteredCategories.length > 0 && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpenSlugs(allExpanded ? [] : ALL_SLUGS)}
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            </div>
          )}

          {filteredCategories.length === 0 ? (
            <EmptyState
              icon={<Search className="h-8 w-8 text-muted-foreground" />}
              title="No results"
              description={`No questions match "${query}". Try a different search, or ask in Slack below.`}
            />
          ) : (
            filteredCategories.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
                query={debouncedQuery}
                openSlugs={openSlugs}
                onOpenSlugsChange={setOpenSlugs}
              />
            ))
          )}

          <StillStuckCallout />
        </div>

        <aside className="hidden lg:block">
          <HelpToc
            categories={filteredCategories}
            activeId={activeCategoryId}
            onSelect={scrollToId}
            className="sticky top-20"
          />
        </aside>
      </div>
    </div>
  );
}
